import BootstrapTable, { ColumnDescription } from 'react-bootstrap-table-next'
import { format } from 'date-fns'
import filterFactory from 'react-bootstrap-table2-filter'
import { ReactComponent as PictoAdresse } from 'assets/default/svg/picto_localisation.svg'
import { ReactComponent as PictoPrix } from 'assets/default/svg/picto_prix.svg'
import { ReactComponent as PictoMaison } from 'assets/default/svg/picto_maison.svg'
import { ReactComponent as PictoNbPieces } from 'assets/default/svg/picto_nb_pieces.svg'
import { ReactComponent as PictoSurfaceHabitable } from 'assets/default/svg/picto_surface_habitable.svg'
import { ReactComponent as PictoCalendrier } from 'assets/default/svg/picto_calendrier.svg'
import SellHistory from 'entity/SellHistory'
import Adresse from 'entity/Adresse'
import TypeLocal from 'components/Enum/TypeLocal/TypeLocal'
import NumberFormat from 'react-number-format'
import cn from 'classnames'
import s from './HistoriqueTable.module.scss'

interface HeaderComponents {
  filterElement: JSX.Element
  sortElement: JSX.Element
}

interface FormatHeaderProps {
  column: ColumnDescription
  components: HeaderComponents
}

interface Props {
  historiqueList: SellHistory[]
}

const HistoriqueTable = ({ historiqueList }: Props): JSX.Element => {
  const formatHeader = (
    { components, column }: FormatHeaderProps,
    picto: JSX.Element
  ): JSX.Element | React.ReactText => (
    <div className={s.colHead}>
      {picto}
      <span>{column.text}</span>
      {components.sortElement}
    </div>
  )

  const sortCaretFn = (order: 'asc' | 'desc' | undefined) => (
    <span
      className={cn(
        s.customSortIcon,
        { [s.asc]: order === 'asc' },
        { [s.desc]: order === 'desc' }
      )}
    />
  )

  const columns: ColumnDescription[] = [
    {
      dataField: 'sellHistory.date',
      text: 'Date de transaction',
      headerFormatter: (
        column: ColumnDescription,
        _,
        components: HeaderComponents
      ) => formatHeader({ column, components }, <PictoCalendrier />),
      formatter: (date: Date) => format(date, 'dd.MM.yyyy'),
      sort: true,
      sortCaret: sortCaretFn,
    },
    {
      dataField: 'sellHistory.price',
      text: 'Prix',
      formatter: (price) => (
        <NumberFormat
          value={price}
          thousandSeparator="&nbsp;"
          decimalSeparator=","
          displayType="text"
          suffix="€"
        />
      ),
      headerFormatter: (
        column: ColumnDescription,
        _,
        components: HeaderComponents
      ) => formatHeader({ column, components }, <PictoPrix />),
      sort: true,
      sortCaret: sortCaretFn,
    },
    {
      dataField: 'sellHistory.typeLocal',
      text: 'Type de logement',
      formatter: (cell) => <TypeLocal value={cell} />,
      headerFormatter: (
        column: ColumnDescription,
        _,
        components: HeaderComponents
      ) => formatHeader({ column, components }, <PictoMaison />),
      sort: true,
      sortCaret: sortCaretFn,
    },
    {
      dataField: 'sellHistory.nbPiece',
      text: 'Nb de pièces',
      formatter: (cell: number) => `${cell} pièce${cell > 1 && 's'}`,
      headerFormatter: (
        column: ColumnDescription,
        _,
        components: HeaderComponents
      ) => formatHeader({ column, components }, <PictoNbPieces />),
      sort: true,
      sortCaret: sortCaretFn,
    },
    {
      dataField: 'sellHistory.surfaceHabitable',
      text: 'Surface Habitable',
      formatter: (surface: number) => `${surface} m²`,
      headerFormatter: (
        column: ColumnDescription,
        _,
        components: HeaderComponents
      ) => formatHeader({ column, components }, <PictoSurfaceHabitable />),
      sort: true,
      sortCaret: sortCaretFn,
    },
    {
      dataField: 'sellHistory.adresse',
      text: 'Adresse',
      formatter: (adresse: Adresse) => `${adresse.voirie}\n ${adresse.commune}`,
      headerFormatter: (
        column: ColumnDescription,
        _,
        components: HeaderComponents
      ) => formatHeader({ column, components }, <PictoAdresse />),
      sort: true,
      sortCaret: sortCaretFn,
    },
  ]

  return (
    <BootstrapTable
      classes={s.table}
      keyField="id"
      data={historiqueList.map((cell, index) => ({
        id: index,
        sellHistory: cell,
      }))}
      columns={columns}
      bordered={false}
      filter={filterFactory()}
    />
  )
}
export default HistoriqueTable
